<template>
  <div class="desktop-filters-container hidden lg:block sticky w-[350px] z-10">
    <div class="bg-card rounded-lg border border-border overflow-hidden shadow-lg h-full flex flex-col">
      <div class="p-6 pb-3 border-b border-border">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-2xl font-semibold">Filters</h2>
          <div class="flex items-center gap-2">
            <!-- AI Search Button -->
            <button 
              @click="toggleAIOverlay"
              class="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
              aria-label="Search with AI"
            >
              <Icon icon="mdi:brain" class="h-4 w-4" />
              AI Search
            </button>
            
            <!-- Clear All Button -->
            <!-- <button 
              v-if="actualActiveFilterCount > 0"
              @click="clearAllFilters"
              class="px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:bg-primary/90 transition-all"
              aria-label="Clear all filters"
            >
              Clear all
            </button> -->
          </div>
        </div>
        
        <!-- Always visible filter actions when filters are active -->
        <div v-if="actualActiveFilterCount > 0" class="p-4 bg-secondary/20 rounded-md">
          <div class="flex items-center justify-between">
            <span class="text-base">{{ actualActiveFilterCount }} active filters</span>
            <button 
              @click="clearAllFilters"
              class="text-base text-red-500 font-bold hover:underline"
              aria-label="Reset all filters"
            >
              Reset all filters
            </button>
          </div>
        </div>
      </div>
      
      <ScrollArea class="flex-1 overflow-hidden scrollarea-hide-scrollbar" viewportClass="h-full">
        <div class="px-6 pt-4 pb-8 scrollarea-body">
          <!-- Active Filters -->
          <ActiveFilters 
            :active-filters="activeFilters"
            :active-filter-count="actualActiveFilterCount"
            @clear-filter="clearFilter"
            @clear-all-filters="clearAllFilters"
          />
          
          <!-- Filter Presets -->
          <FilterPresets class="mb-6 border-b border-border pb-4" />
          
          <!-- Saved Filters -->
          <SavedFilters class="mb-6 border-b border-border pb-4" />
          
          <!-- Suggested Filters -->
          <SuggestedFilters 
            @expand-and-scroll-to-filter="scrollToFilterSection"
            class="mb-6" 
          />
          
          
          <!-- Filter Sections -->
          <div class="space-y-6">
            <FilterSection
              v-for="section in filterSections" 
              :key="section.id"
              :section="section"
              :active-filters="activeFilters"
              :is-expanded="expandedSections.has(section.id)"
              @toggle-section="toggleSection"
              @update-filter="updateFilter"
              @clear-filter="clearFilter"
            >
              <!-- Forward all slots received from parent (VehicleFilters) -->
              <template v-for="(_, slotName) in $slots" #[slotName]>
                <slot :name="slotName"></slot>
              </template>
            </FilterSection>
          </div>
          
          <!-- Filter Distribution -->
          <FilterDistribution :active-filters="activeFilters" class="mt-6 mb-10" />
        </div>
      </ScrollArea>
    </div>

    <!-- AI Search Overlay -->
    <AIFilterOverlay 
    class="w-[340px]"
      :is-open="isAIOverlayOpen"
      :current-filters="activeFilters"
      @close="closeAIOverlay"
      @apply-filters="handleAIFilters"
      @clear-all="clearAllFilters"
      @ai-vehicles-applied="handleAIVehiclesApplied"
    />
  </div>
</template>

<script setup lang="ts">
import ActiveFilters from '~/layers/base/components/filters/ActiveFilters.vue'
import FilterSection from '~/layers/base/components/filters/FilterSection.vue'
import MakeModelBadgeFilter from '~/layers/base/components/filters/MakeModelBadgeFilter.vue'
import FilterPresets from '~/layers/base/components/filters/FilterPresets.vue'
import SavedFilters from '~/layers/base/components/filters/SavedFilters.vue'
import SuggestedFilters from '~/layers/base/components/filters/SuggestedFilters.vue'
import FilterDistribution from '~/layers/base/components/filters/FilterDistribution.vue'
import { type FilterSection as FilterSectionType } from '~/layers/base/composables/useFilters'
import { ScrollArea } from '~/layers/base/components/ui/scroll-area'
import { useFilters } from '~/layers/base/composables/useFilters'
import { computed, nextTick, onMounted, ref } from 'vue'
import { Icon } from '@iconify/vue'
import AIFilterOverlay from './AIFilterOverlay.vue'
import { useVehiclesStore } from '~/stores/vehicles'

const props = defineProps<{
  activeFilters: Record<string, any>;
  activeFilterCount: number;
  filterSections: FilterSectionType[];
  expandedSections: Set<string>;
}>()

const emit = defineEmits<{
  'toggle-section': [sectionId: string];
  'update-filter': [filterName: string, value: any];
  'clear-filter': [filterName: string];
  'clear-all-filters': [];
}>()

const { countActiveFilters } = useFilters()

// AI Overlay state
const isAIOverlayOpen = ref(false)

// Computed property to get the actual number of active filters that should be displayed
const actualActiveFilterCount = computed(() => {
  return countActiveFilters(props.activeFilters)
})

// AI Overlay methods
const toggleAIOverlay = () => {
  isAIOverlayOpen.value = !isAIOverlayOpen.value
}

const closeAIOverlay = () => {
  isAIOverlayOpen.value = false
}

const handleAIFilters = (filters: any) => {
  // Apply AI-generated filters
  Object.entries(filters).forEach(([filterName, value]) => {
    if (value !== null && value !== undefined) {
      emit('update-filter', filterName, value)
    }
  })
  
  // Don't close overlay - let user continue chatting after applying filters
}

const handleAIVehiclesApplied = async (data: { query: string; vehicleCount: number }) => {
  console.log('🚀 [DesktopFilters] AI vehicles applied:', data)
  
  // Wait for the next tick to ensure the DOM is updated with new results
  await nextTick()
  
  // Add a small delay to ensure all components have finished updating
  setTimeout(() => {
    // Scroll to top of the page to show the new results
    if (typeof window !== 'undefined') {
      window.scrollTo({ 
        top: 0, 
        behavior: 'smooth' 
      })
      console.log('📜 [DesktopFilters] Scrolled to top after AI applied', data.vehicleCount, 'vehicles')
    }
  }, 100) // Small delay to ensure all updates are complete
}

// Check if we need to clear filters when on cars-for-sale with no query params
onMounted(() => {
  try {
    const route = useRoute()
    if (route.path === '/cars-for-sale' && 
        Object.keys(route.query).length === 0 && 
        actualActiveFilterCount.value > 0) {
      clearAllFilters()
    }
    
    // Add a listener for route changes instead of using a watcher
    const router = useRouter()
    router.afterEach((to) => {
      if (to.path === '/cars-for-sale' && 
          Object.keys(to.query).length === 0 && 
          actualActiveFilterCount.value > 0) {
        clearAllFilters()
      }
    })
  } catch (error) {
    console.error('Error in DesktopFilters route handling:', error)
  }
})

const toggleSection = (sectionId: string) => {
  emit('toggle-section', sectionId)
}

const updateFilter = (filterName: string, value: any) => {
  emit('update-filter', filterName, value)
}

const clearFilter = (filterName: string) => {
  emit('clear-filter', filterName)
}

const clearAllFilters = () => {
  console.log('🗑️ [DesktopFilters] Clearing all filters including AI filter')
  
  // Clear AI filter state directly in the store
  const vehiclesStore = useVehiclesStore()
  vehiclesStore.clearAIFilter()
  
  // Emit clear-all-filters event to parent (VehicleFilters.vue)
  emit('clear-all-filters')
  
  // Keep the MakeModelBadgeFilter clearing logic if it's necessary and works
  const makeModelFilter = document.querySelector('[data-filter-name="make_model"]')
  if (makeModelFilter) {
    const event = new CustomEvent('clear-make-model-badge')
    makeModelFilter.dispatchEvent(event)
  }
}

const scrollToFilterSection = (filterNameFromSuggested: string) => {
  // Find the section that contains this filterName
  const targetSection = props.filterSections.find(section => section.filters.includes(filterNameFromSuggested));

  if (!targetSection) {
    console.warn(`[DesktopFilters] No section found containing filter: ${filterNameFromSuggested}`);
    return;
  }

  const sectionIdToScroll = targetSection.id;

  // Ensure the section is expanded
  if (!props.expandedSections.has(sectionIdToScroll)) {
    emit('toggle-section', sectionIdToScroll)
  }

  // Wait for the DOM to update after potential section expansion
  nextTick(() => {
    // const scrollAreaViewport = document.querySelector('.desktop-filters .scrollarea-hide-scrollbar')
    // The ScrollAreaRoot is '.scrollarea-hide-scrollbar'. The actual scrollable viewport is its child with data-radix-scroll-area-viewport.
    const scrollAreaRoot = document.querySelector('.desktop-filters .scrollarea-hide-scrollbar');
    const scrollAreaViewport = scrollAreaRoot ? scrollAreaRoot.querySelector('[data-radix-scroll-area-viewport]') : null;

    const targetSectionElement = document.querySelector(`.desktop-filters [data-section-id="${sectionIdToScroll}"]`)

    if (scrollAreaViewport && targetSectionElement) {
      // const targetOffsetTop = (targetSectionElement as HTMLElement).offsetTop;
      // const scrollAreaOffsetTop = (scrollAreaViewport as HTMLElement).offsetTop; // This is now the viewport, not the root
      
      // The true scroll container is the scrollAreaViewport itself, or a direct child if Radix wraps content further.
      // For Radix, the targetSectionElement should be a descendant of scrollAreaViewport.
      // We need the offset of targetSectionElement relative to the scrollAreaViewport's scrollable content.
      
      // Check if targetSectionElement is a direct child or nested. 
      // offsetTop of targetSectionElement relative to scrollAreaViewport's offsetParent is complex if they don't share one.
      // A simpler approach: getBoundingClientRect for both and use their top values.

      const viewportRect = scrollAreaViewport.getBoundingClientRect();
      const targetRect = targetSectionElement.getBoundingClientRect();

      // scrollTop needs to be the difference between the target's top and the viewport's top, plus current scroll position.
      const scrollTopToTarget = targetRect.top - viewportRect.top + scrollAreaViewport.scrollTop;
      
      scrollAreaViewport.scrollTo({
        top: scrollTopToTarget - 20, // Add a 20px offset from the top
        behavior: 'smooth'
      });

    } else {
      console.warn(`[DesktopFilters] Could not find elements for scrolling. Root: ${!!scrollAreaRoot}, Viewport: ${!!scrollAreaViewport}, SectionEl: ${!!targetSectionElement} for section ID: ${sectionIdToScroll} (filter: ${filterNameFromSuggested})`);
    }
  })
}
</script>

<style scoped>
.header-not-sticky .desktop-filters{
  top: 8.5rem;
  height: calc(100vh - 9rem);
} 
/* Styles removed, relying on RecycleScroller props */
body.header-is-sticky .desktop-filters{
  height: calc(100vh - 1rem);
}

/* Desktop Filters Sticky Container */
.desktop-filters-container {
  position: sticky;
  left: 0;
  padding-right: 1rem;
  
  /* Default positioning when header is visible */
  top: 1.5rem;
  height: calc(100vh - 13rem);
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* When header is sticky (hidden), adjust positioning to fill full viewport */
:global(body.header-is-sticky) .desktop-filters-container {
  top: 0 !important;
  height: 100vh !important;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* Alternative selector for when header is scrolled/hidden */
:global(body.header-hidden) .desktop-filters-container {
  top: 0 !important;
  height: 100vh !important;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* Alternative selector if using different header state classes */
:global(.header-scrolled) .desktop-filters-container,
:global(.header-hidden) .desktop-filters-container {
  top: 0 !important;
  height: 100vh !important;
}

/* Ensure sticky behavior works on all browsers */
.desktop-filters-container {
  position: -webkit-sticky; /* Safari */
  position: sticky;
}

/* Add Tesla-inspired styles */
.shadow-lg {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Add a slight backdrop blur in dark mode for a premium feel */
.dark .bg-card {
  backdrop-filter: blur(8px);
  background-color: hsla(var(--card) / 0.95);
}

/* Hide scrollbar by default but keep scrolling functionality */
.scrollarea-hide-scrollbar::-webkit-scrollbar {
  width: 0px;
  background: transparent;
}

.scrollarea-hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

/* Custom scrollbar styling for ScrollArea component */
:deep(.scrollbar) {
  opacity: 0;
  transition: opacity 0.2s ease;
  width: 6px !important;
  right: 2px !important;
}

:deep(.viewport):hover + .scrollbar,
:deep(.scrollbar:hover) {
  opacity: 1;
}

:deep(.scrollbar-thumb) {
  background-color: hsl(var(--muted-foreground) / 0.3) !important;
  border-radius: 6px !important;
  border: none !important;
  transition: background-color 0.2s ease;
}

:deep(.scrollbar-thumb:hover) {
  background-color: hsl(var(--muted-foreground) / 0.5) !important;
}

:deep(.dark .scrollbar-thumb) {
  background-color: hsl(var(--muted-foreground) / 0.4) !important;
}

:deep(.dark .scrollbar-thumb:hover) {
  background-color: hsl(var(--muted-foreground) / 0.6) !important;
}

/* Override the min-width applied by radix-vue ScrollArea */
:deep([data-radix-scroll-area-viewport]) > div {
  min-width: unset !important;
  width: 100% !important;
}

/* Debug: Add temporary border to see if positioning is working */
.desktop-filters-container {
  /* Uncomment for debugging: border: 2px solid red; */
}
</style>
